import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { resolveShopSlugFromRoute } from '~/utils/resolveShopSlug'
import { captureSentryException } from '~/utils/sentry'
import { resolveShopApiBase } from '~/utils/shopApi'

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

let isRefreshing = false
let refreshSubscribers: Array<() => void> = []

function subscribeTokenRefresh(cb: () => void) {
  refreshSubscribers.push(cb)
}

function onTokenRefreshed() {
  refreshSubscribers.forEach(cb => cb())
  refreshSubscribers = []
}

export default defineNuxtPlugin((nuxtApp) => {
  const authStore = useAuthStore()
  // Capture once in plugin context — never call useState/useRoute/useRuntimeConfig inside axios interceptors
  const runtimeConfig = useRuntimeConfig()
  const domainState = useState<{
    host: string
    shop: { slug?: string } | null
    resolved: boolean
  }>('shop-domain', () => ({
    host: '',
    shop: null,
    resolved: false,
  }))

  const api = axios.create({
    baseURL: '/',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    timeout: 30000,
    withCredentials: true,
  })

  api.interceptors.request.use((reqConfig) => {
    const route = nuxtApp.$router.currentRoute.value
    const domainSlug = domainState.value?.shop?.slug || ''
    // Custom domains: same-origin /api so auth cookies are first-party.
    const sameOriginApi = Boolean(domainSlug) && import.meta.client

    const shop = resolveShopSlugFromRoute(
      route.path,
      route.params as Record<string, string | string[]>,
      domainSlug,
    )

    // Never send shop/admin traffic to the platform API when slug is missing.
    // Prefer same-origin /api (Host resolves shop) over /api/platform.
    if (!shop && !route.path.startsWith('/platform')) {
      reqConfig.baseURL = sameOriginApi || import.meta.client
        ? '/api'
        : String(runtimeConfig.public.apiOrigin || '').replace(/\/$/, '') + '/api'
    } else {
      reqConfig.baseURL = resolveShopApiBase(shop, {
        apiOrigin: String(runtimeConfig.public.apiOrigin || ''),
        platformApiUrl: String(runtimeConfig.public.platformApiUrl || runtimeConfig.public.apiUrl || ''),
        sameOriginApi,
      })
    }

    return reqConfig
  })

  api.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryConfig | undefined

      const hadSession = Boolean(authStore.user || authStore.hasSession)

      if (
        import.meta.client
        && hadSession
        && error.response?.status === 401
        && originalRequest
        && !originalRequest._retry
        && !originalRequest.url?.includes('/auth/login')
        && !originalRequest.url?.includes('/auth/refresh')
        && !originalRequest.url?.includes('/auth/oauth/exchange')
      ) {
        if (isRefreshing) {
          return new Promise((resolve) => {
            subscribeTokenRefresh(() => {
              resolve(api(originalRequest))
            })
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const refreshed = await authStore.refreshTokens()
          if (refreshed) {
            onTokenRefreshed()
            return api(originalRequest)
          }
        } catch {
          authStore.clearAuth()
        } finally {
          isRefreshing = false
        }
      }

      const status = error.response?.status
      // 401 from token refresh is expected; everything else (including 400 validation)
      // should still land in Sentry so we can see the API body.
      if (status && status !== 401) {
        const route = nuxtApp.$router.currentRoute.value
        void captureSentryException(error, {
          url: error.config?.url,
          method: error.config?.method,
          status,
          apiResponse: error.response?.data,
          shopSlug: resolveShopSlugFromRoute(
            route.path,
            route.params as Record<string, string | string[]>,
            domainState.value?.shop?.slug || '',
          ),
        })
      }

      return Promise.reject(error)
    },
  )

  return {
    provide: {
      api,
    },
  }
})

declare module '#app' {
  interface NuxtApp {
    $api: ReturnType<typeof axios.create>
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $api: ReturnType<typeof axios.create>
  }
}
